import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { formatUsd } from '../_helpers/currency.js';
import { fetchWithFallback, isConfirmedTip, isPlayedRequest, tipAmount } from '../_helpers/listenerActivity.js';
import { resolveMediaUrl } from '../_helpers/mediaUrl.js';
import { SongStatus } from '../_helpers/songStatus.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { DEFAULT_LOCALE, englishT } from '../locales/index.js';
import config from '../config.js';

// resolveMediaUrl lived here first; callers still import it from this module.
export { resolveMediaUrl };

export const MAX_QUEUE_PREVIEW = 6;
export const MAX_NOTIFICATIONS = 3;
export const QUEUE_POLL_INTERVAL_MS = 10000;
export const NOTIFICATIONS_SEEN_KEY = 'listenerNotificationsSeenAt';
/** Catalog message for the predicted-events card's error state: `{ key, params }` for `t()`. */
export const PREDICTED_EVENTS_ERROR_MESSAGE = Object.freeze({ key: 'home.predicted.loadError', params: {} });
/** English copy of PREDICTED_EVENTS_ERROR_MESSAGE, kept for callers that compare the text. */
export const PREDICTED_EVENTS_ERROR = englishT(PREDICTED_EVENTS_ERROR_MESSAGE.key);
/** Catalog message for the recommendations failure in the hero slot. */
export const DASHBOARD_ERROR_MESSAGE = Object.freeze({ key: 'home.state.loadError', params: {} });

const NEW_NOTIFICATION_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Tip amount as the listener sees it ("$5.00"): always with cents, so the
 * ledger lines up, unlike the streamer's glanceable "$5" (see `formatUsd`).
 */
export const formatTipAmount = (amount, locale = DEFAULT_LOCALE) => formatUsd(amount, locale, { cents: 'always' });

/**
 * Normalise the snake_case `BandResponse` (`/bands/recommended`, and the `band`
 * of each `/home/feed` entry; see PerformersSearchPage) and the camelCase inline band summary on `/me/predicted-events`. `t` names
 * a band the server left nameless (English without one).
 */
export const normalizeBand = (band, t = englishT) => ({
  ID: band.ID ?? band.id,
  Name: band.Name ?? band.name ?? t('home.fallbacks.unknownPerformer'),
  ProfileImageURL: resolveMediaUrl(band.ProfileImageURL ?? band.profileImageURL ?? band.profile_image_url ?? null),
  SocialMediaLinks: band.SocialMediaLinks ?? band.social_media_links ?? null,
  TwitchChannel: band.TwitchChannel ?? band.twitch_channel ?? null,
  Location: band.Location ?? band.location ?? null,
});

/**
 * `PublicQueueSong.release_year` (ENG-730): a UTC year, left out when unknown.
 * Anything that is not a positive whole number reads as unknown.
 */
const releaseYear = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const year = Number(value);
  return Number.isInteger(year) && year > 0 ? year : null;
};

/** Same queue shape StreamPage / PublicSongList read from `/public/bands/{id}/queue`. */
export const formatQueue = (data, t = englishT) =>
  (Array.isArray(data) ? data : []).map((item) => ({
    RequestId: item.ID ?? item.id,
    Position: item.Position ?? item.position ?? 0,
    Title: item.song?.Title ?? item.song?.title ?? t('common.fallbacks.unknownTitle'),
    Artist: item.song?.Artist ?? item.song?.artist ?? t('common.fallbacks.unknownArtist'),
    ReleaseYear: releaseYear(item.song?.release_year ?? item.song?.ReleaseYear),
    ArtworkURL: resolveMediaUrl(item.song?.artwork_url ?? null),
    RequestedBy: item.requested_by ?? item.RequestedBy ?? '',
    Status: item.Status ?? item.status,
  }));

export const splitQueue = (queue) => {
  const current = queue.find((item) => item.Status === SongStatus.PLAYING) ?? null;
  const upcoming = queue
    .filter((item) => item.Status === SongStatus.QUEUED)
    .sort((a, b) => a.Position - b.Position)
    .slice(0, MAX_QUEUE_PREVIEW);
  return { current, upcoming };
};

/** A random member of `items`, or null when it is empty. */
const randomFrom = (items) => (items.length === 0 ? null : items[Math.floor(Math.random() * items.length)]);

/**
 * The `/home/feed` match score (ENG-730): an integer 1-100, or null when the
 * match was not picked by score. The backend never sends 0; anything that is
 * not a positive number is treated as "no score", so the eyebrow never shows
 * "0%" or an empty percentage.
 */
export const normalizeMatchScore = (score) => {
  if (typeof score !== 'number' || !Number.isFinite(score) || score < 1) return null;
  return Math.min(100, Math.round(score));
};

/** Live snapshot of a feed entry, in the shape `HomePage` merges with `useStreamStatus`. */
const entryStatus = (entry) => ({
  isLive: Boolean(entry.isLive),
  viewerCount: Number(entry.viewerCount ?? 0) || 0,
  title: null,
  // Server-built player URL; offline fallbacks never carry one (ENG-730).
  embedUrl: entry.embedUrl || null,
});

/**
 * Turn a `GET /home/feed` response (ENG-730: `{ match, biggestCrowd,
 * randomPool, liveCount }`, each entry `{ band, isLive, viewerCount,
 * embedUrl? }`, the match with a `score`) into what the dashboard renders.
 * The backend picks the match and the biggest crowd; the client only picks
 * the random band from the pool.
 *
 *  - `featured`: `{ match, biggestCrowd, matchScore }` (bands via normalizeBand).
 *  - `shuffleSet`: the pool's live bands when there are at least two of them,
 *    otherwise the whole pool (live first, then offline top-ups).
 *  - `liveIds`: the ids of every live band in the feed.
 *  - `statusById`: each band's `isLive`, `viewerCount` and `embedUrl`.
 *
 * Offline fallback entries carry `isLive: false`, `viewerCount: 0` and no
 * `embedUrl`. The stream embed only frames `embedUrl` while a band is live,
 * so they render the same offline placeholder (or a Twitch channel from the
 * band's links) as before, without a `/stream/status` call (ENG-323).
 */
export const normalizeHomeFeed = (response, t = englishT) => {
  const statusById = {};
  const liveIds = new Set();
  const read = (entry) => {
    if (!entry?.band) return null;
    const band = normalizeBand(entry.band, t);
    if (band.ID == null || statusById[band.ID]) return null;
    statusById[band.ID] = entryStatus(entry);
    if (statusById[band.ID].isLive) liveIds.add(band.ID);
    return band;
  };
  const match = read(response?.match);
  const biggestCrowd = read(response?.biggestCrowd);
  const pool = (Array.isArray(response?.randomPool) ? response.randomPool : []).map(read).filter(Boolean);
  const livePool = pool.filter((band) => liveIds.has(band.ID));
  return {
    featured: { match, biggestCrowd, matchScore: match ? normalizeMatchScore(response.match.score) : null },
    shuffleSet: livePool.length >= 2 ? livePool : pool,
    liveIds,
    statusById,
  };
};

/**
 * The first random pick: a random member of the shuffle set, preferring its
 * live bands so a live band in the pool shows whenever the feed has one.
 */
export const pickInitialRandom = (shuffleSet, liveIds) =>
  randomFrom(shuffleSet.filter((band) => liveIds.has(band.ID))) ?? randomFrom(shuffleSet);

/** Shuffle: a different member of the shuffle set, or the current pick when there is none. */
export const pickNextRandom = (shuffleSet, current) =>
  randomFrom(shuffleSet.filter((band) => band.ID !== current?.ID)) ?? current;

/**
 * `/me/predicted-events` (ENG-329) responds with `{ events, total }` where each
 * event is `{ type, id, startsAt, title, location, isOnline, band: { id, name,
 * profileImageURL } }`. The list is final — the prediction algorithm owns
 * selection, order and cap — so it is rendered as received: no day filter, no
 * re-sort, no slice. Only what the card renders is kept. `type` ("scheduled"
 * today; inferred kinds later) passes through untouched — a missing one stays
 * unknown rather than being promoted to a fact — so the card can tell kinds
 * apart without a contract change. Entries the card could not render (no band,
 * unparseable start) are dropped.
 */
export const normalizePredictedEvents = (response, t = englishT) => {
  const events = Array.isArray(response?.events) ? response.events : [];
  return events.flatMap((event) => {
    const startsAt = new Date(event.startsAt);
    const band = event.band ? normalizeBand(event.band, t) : null;
    if (Number.isNaN(startsAt.getTime()) || band?.ID == null) return [];
    return [
      {
        id: event.id ?? `${band.ID}-${startsAt.getTime()}`,
        type: event.type ?? null,
        location: event.location ?? null,
        startsAt,
        band,
      },
    ];
  });
};

/**
 * Merge the listener's requests and confirmed tips (the rows of `/user/requests`
 * and `/user/tips`, read with listenerActivity.js) into a newest-first activity feed.
 * Items are "new" when they post-date the listener's last visit; on a first
 * visit anything from the last 24h counts. `limit` caps the returned items
 * (the home card shows three; the /notifications inbox passes Infinity).
 *
 * Each item carries its row text twice: `message` is the catalog descriptor
 * (`{ key, params }`) a component renders through its own `t()`, and `label`
 * is that message already resolved with the `t` / `locale` given here
 * (English without them) for callers that only read text (ENG-387).
 */
export const buildNotifications = (
  requests,
  tips,
  now = new Date(),
  seenAt = null,
  limit = MAX_NOTIFICATIONS,
  { t = englishT, locale = DEFAULT_LOCALE } = {}
) => {
  const items = [];
  for (const request of Array.isArray(requests) ? requests : []) {
    const at = new Date(request.requestedAt ?? request.requested_at ?? request.createdAt ?? request.created_at);
    if (Number.isNaN(at.getTime())) continue;
    const played = isPlayedRequest(request);
    const message = {
      key: played ? 'home.notifications.played' : 'home.notifications.requested',
      params: { title: request.title || t('common.fallbacks.unknownSong') },
    };
    items.push({
      id: `request-${request.id ?? request.ID ?? at.getTime()}`,
      kind: 'request',
      message,
      label: t(message.key, message.params),
      bandName: request.bandName ?? request.band_name ?? null,
      at,
    });
  }
  for (const tip of Array.isArray(tips) ? tips : []) {
    if (!isConfirmedTip(tip)) continue;
    const at = new Date(tip.createdAt ?? tip.created_at);
    if (Number.isNaN(at.getTime())) continue;
    const message = {
      key: 'home.notifications.tipped',
      params: {
        amount: formatTipAmount(tipAmount(tip) ?? 0, locale),
        band: tip.bandName ?? tip.band_name ?? t('home.fallbacks.performer'),
      },
    };
    items.push({
      id: `tip-${tip.id ?? tip.ID ?? at.getTime()}`,
      kind: 'tip',
      message,
      label: t(message.key, message.params),
      bandName: tip.bandName ?? tip.band_name ?? null,
      at,
    });
  }
  const sorted = items.sort((a, b) => b.at - a.at);
  const seen = seenAt instanceof Date && !Number.isNaN(seenAt.getTime()) ? seenAt : null;
  const isNew = (item) => (seen ? item.at > seen : now - item.at <= NEW_NOTIFICATION_WINDOW_MS);
  return { items: sorted.slice(0, limit), newCount: sorted.filter(isNew).length };
};

const settled = (result, fallback) => (result.status === 'fulfilled' ? result.value : fallback);

const readSeenAt = () => {
  try {
    const raw = localStorage.getItem(NOTIFICATIONS_SEEN_KEY);
    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
  } catch {
    return null;
  }
};

const writeSeenAt = (date) => {
  try {
    localStorage.setItem(NOTIFICATIONS_SEEN_KEY, date.toISOString());
  } catch {
    // Storage unavailable — the 24h fallback still applies next visit.
  }
};

/**
 * Loads everything the listener dashboard (/home) needs: the featured
 * performers from `GET /home/feed` (ENG-730) with their live status (including
 * the server-built `embedUrl` for the stream cards) and the match score, the queues of the three featured performers, the listener's predicted
 * upcoming events and their recent activity.
 *
 * `error` and `predictedEventsError` are catalog descriptors (`{ key, params }`
 * or null) so the page renders them through its own `t()` and a locale switch
 * re-translates them without a refetch.
 */
export function useListenerDashboard() {
  const fetchWrapperRef = useRef(useFetchWrapper());
  // Latest `t` / `locale` for the loaders' fallback names and amounts, read
  // through a ref so a locale switch does not re-run the effects.
  const { t, locale } = useLocale();
  const i18nRef = useRef({ t, locale });
  i18nRef.current = { t, locale };

  const [shuffleSet, setShuffleSet] = useState([]);
  const [statusById, setStatusById] = useState({});
  const [featured, setFeatured] = useState({ match: null, biggestCrowd: null, randomPick: null, matchScore: null });
  const [queuesById, setQueuesById] = useState({});
  const [predictedEvents, setPredictedEvents] = useState([]);
  const [predictedEventsLoading, setPredictedEventsLoading] = useState(true);
  const [predictedEventsError, setPredictedEventsError] = useState(null);
  const [notifications, setNotifications] = useState({ items: [], newCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [predictedReloadKey, setPredictedReloadKey] = useState(0);

  // Featured performers + live status: one `/home/feed` call (ENG-730)
  // replaced `/bands/recommended` and its per-band `/stream/status` fan-out.
  useEffect(() => {
    let cancelled = false;
    const api = config.apiBaseUrl;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchWrapperRef.current.get(`${api}/home/feed`);
        if (cancelled) return;
        // Failures come back as a 500 (rejected by fetchWrapper); an envelope
        // that still says `success: false` is treated the same way.
        if (response?.success === false) throw new Error(response.error || 'home feed failed');
        const feed = normalizeHomeFeed(response, i18nRef.current.t);

        setShuffleSet(feed.shuffleSet);
        setStatusById(feed.statusById);
        setFeatured({ ...feed.featured, randomPick: pickInitialRandom(feed.shuffleSet, feed.liveIds) });
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading listener dashboard:', err);
        // Only the featured performers failed; predicted events and
        // notifications load on their own (ENG-328).
        setError(DASHBOARD_ERROR_MESSAGE);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Predicted upcoming events — one listener-scoped request (ENG-333). This
  // replaced a per-recommended-band `/bands/{id}/shows` fan-out that 401'd by
  // construction (member-gated) and had to bypass fetchWrapper to survive it.
  // `/me/predicted-events` is the caller's own resource, so it goes through
  // fetchWrapper like every other call: a 401 there really is an expired
  // session. Any other failure is scoped to the card, not the dashboard, and
  // the card can retry it on its own (predictedReloadKey) without re-spinning
  // the recommendations.
  useEffect(() => {
    let cancelled = false;
    const api = config.apiBaseUrl;
    const load = async () => {
      // While the answer is unknown the card must not claim "nothing tonight".
      setPredictedEventsLoading(true);
      setPredictedEventsError(null);
      try {
        const response = await fetchWrapperRef.current.get(`${api}/me/predicted-events`);
        if (cancelled) return;
        setPredictedEvents(normalizePredictedEvents(response, i18nRef.current.t));
      } catch {
        if (cancelled) return;
        setPredictedEvents([]);
        setPredictedEventsError(PREDICTED_EVENTS_ERROR_MESSAGE);
      }
      setPredictedEventsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, predictedReloadKey]);

  // Listener activity → notifications
  useEffect(() => {
    let cancelled = false;
    const api = config.apiBaseUrl;
    const load = async () => {
      const [requests, tips] = await Promise.allSettled([
        fetchWithFallback(fetchWrapperRef.current, `${api}/user/requests`, 'requests'),
        fetchWithFallback(fetchWrapperRef.current, `${api}/user/tips`, 'tips'),
      ]);
      if (cancelled) return;
      const now = new Date();
      setNotifications(
        buildNotifications(settled(requests, []), settled(tips, []), now, readSeenAt(), MAX_NOTIFICATIONS, i18nRef.current)
      );
      writeSeenAt(now);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const featuredIds = useMemo(
    () => [featured.match, featured.biggestCrowd, featured.randomPick].filter(Boolean).map((band) => band.ID),
    [featured]
  );
  const featuredKey = featuredIds.join(',');

  // Queues (polled) for the featured performers
  useEffect(() => {
    if (!featuredKey) return undefined;
    const ids = featuredKey.split(',');
    let cancelled = false;
    const api = config.apiBaseUrl;

    const fetchQueues = async () => {
      const results = await Promise.allSettled(
        ids.map((id) => fetchWrapperRef.current.get(`${api}/public/bands/${id}/queue`))
      );
      if (cancelled) return;
      setQueuesById((previous) => {
        const next = { ...previous };
        ids.forEach((id, index) => {
          if (results[index].status === 'fulfilled') next[id] = splitQueue(formatQueue(results[index].value, i18nRef.current.t));
        });
        return next;
      });
    };

    fetchQueues();
    const interval = setInterval(fetchQueues, QUEUE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [featuredKey]);

  const shuffleRandomPick = useCallback(() => {
    setFeatured((previous) => {
      const next = pickNextRandom(shuffleSet, previous.randomPick);
      return next && next !== previous.randomPick ? { ...previous, randomPick: next } : previous;
    });
  }, [shuffleSet]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const reloadPredictedEvents = useCallback(() => setPredictedReloadKey((key) => key + 1), []);

  return {
    statusById,
    featured,
    queuesById,
    predictedEvents,
    predictedEventsLoading,
    predictedEventsError,
    notifications,
    loading,
    error,
    // Shuffle needs another member of the shuffle set to swap in.
    canShuffle: shuffleSet.length >= 2,
    shuffleRandomPick,
    reload,
    reloadPredictedEvents,
  };
}
